import React from "react";
import {
  PieChart,
  Pie,
  Legend,
  ResponsiveContainer,
  Label,
  LabelList,
  Tooltip,
} from "recharts";
import useProperties from "hooks/useProperties";
import { capitalizeWord } from "utils/commonUtils";
import { formatAbbreviationNumber } from "utils/formatNumber";
import { ReactComponent as MapIcon } from "assets/icons/location_big.svg";

const renderColorfulLegendText = (value, entry) => {
  const percentage = entry.payload.percent * 100;

  const formattedPercentage =
    percentage % 1 === 0
      ? `${percentage.toFixed(0)}%`
      : `${percentage.toFixed(1)}%`;

  return (
    <>
      <span className="text-black text-[14px] font-medium leading-[16px]">
        {value}
      </span>
      <span className="text-[#7C7C7C] text-[14px] font-medium leading-[16px] ml-[5px]">
        {formattedPercentage}
      </span>
    </>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip bg-white p-2 border rounded-lg shadow-sm ">
        <p className="">
          {payload[0].name}:{" "}
          <span className="text-primary font-bold">{payload[0].value}</span>
        </p>
      </div>
    );
  }

  return null;
};

const colors = ["#35B9E9", "#E0C0FF", "#B4B2FA", "#FBB3B4", "#ff0000"];

const PropertyDistributionChart = ({
  data = null,
  emptyStateMessage = "Seleziona una zona per visualizzarne le statistiche di mercato",
}) => {
  const { dashboardMarketStatisticsProperties } = useProperties();

  // Use provided data or fallback to dashboard data
  const sourceData = data || dashboardMarketStatisticsProperties;

  // Transform the data for the chart
  const chartData = sourceData.map((item, index) => ({
    location: capitalizeWord(item.locationName), // Use locationName for X-axis
    value: parseFloat(item.propertiesCount), // Use averageArea as the value
    fill: colors[index % colors.length],
  }));

  const totalProperties = chartData.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className="bg-white flex-1 rounded-[16px] px-4 pb-4 pt-[21.5px] h-[288px] border-[1px] border-[#E2E4E9]">
      {chartData?.length > 0 ? (
        <>
          <h2 className="text-[16px] font-semibold leading-5 text-[#1A202C] -tracking-[0.176px]">
            Distribuzione Annunci
          </h2>
          <div className="mt-4 h-[200px] w-full">
            <ResponsiveContainer
              className="relative dashboard_donut_chart"
              width="100%"
              height={"100%"}
            >
              <PieChart>
                <Legend
                  height={36}
                  iconType="circle"
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  className="!top-0 !right-0 !h-auto"
                  iconSize={10}
                  formatter={renderColorfulLegendText}
                  width={140}
                />

                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "transparent" }}
                />
                <Pie
                  data={chartData}
                  dataKey="value"
                  cornerRadius={8}
                  nameKey="location"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  labelLine={false}
                >
                  <LabelList
                    dataKey="value"
                    position="inside"
                    fontSize={10}
                    fontWeight={500}
                    color="white"
                    fill="white"
                    formatter={(value, entry) => {
                      const percentage = (value / totalProperties) * 100;
                      return percentage % 1 === 0
                        ? `${percentage.toFixed(0)}%`
                        : `${percentage.toFixed(1)}%`;
                    }}
                  />

                  <Label
                    value={formatAbbreviationNumber(totalProperties)}
                    fontSize={24}
                    fontWeight={700}
                    fill="#1E1B39"
                    position="center"
                  />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="flex h-full items-center justify-center flex-col">
          <MapIcon className="w-[63px]  text-[#1DA1F266]" />
          <p className="text-[16px] text-center mt-4 font-semibold leading-[20px] text-[#A5D9FA]">
            {emptyStateMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyDistributionChart;
